/**
 * Thuysvers Survey Form Utilities
 * Shared functions for thuysvers-survey-forms.js and thuysvers-subscription-survey.js
 */
(function () {
  'use strict';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STORAGE_KEY_RECOMMENDATIONS = 'recommendations';
const CHURNED_RECOMMENDATIONS = 'churned_recommendations';
const EVENT_RECOMMENDATIONS_UPDATED = 'ecomm-recommendations-updated';

/** Returns the currently active survey step element. @param {ThuysversSurveyFormContext} ctx @returns {HTMLElement | undefined} */
function getActiveStep(ctx) {
  return ctx.refs.surveyStep?.find((/** @type {HTMLElement} */ step) => step.classList.contains('active'));
}

/** Returns the zero-based index of the currently active survey step. @param {ThuysversSurveyFormContext} ctx @returns {number} */
function getActiveStepIndex(ctx) {
  return ctx.refs.surveyStep?.findIndex((/** @type {HTMLElement} */ step) => step.classList.contains('active')) ?? -1;
}

/** Sets the active survey step by index, updating CSS classes. @param {ThuysversSurveyFormContext} ctx @param {number} index */
function setActiveStepIndex(ctx, index) {
  if (!Array.isArray(ctx.refs.surveyStep)) return;
  if (index < 0 || index >= ctx.refs.surveyStep.length) return;

  const currentIndex = getActiveStepIndex(ctx);
  if (currentIndex === index) return;

  ctx.refs.surveyStep[currentIndex]?.classList.remove('active');
  ctx.refs.surveyStep[index]?.classList.add('active');
}

/** Validates email input against regex and sets custom validity; returns whether the input is valid. @param {HTMLInputElement} emailInput @returns {boolean} */
function syncEmailValidity(emailInput) {
  const value = emailInput.value.trim();

  if (!value) {
    emailInput.setCustomValidity('');
    return emailInput.checkValidity();
  }

  if (!EMAIL_REGEX.test(value)) {
    emailInput.setCustomValidity('Please enter a valid email address.');
    return false;
  }

  emailInput.setCustomValidity('');
  return emailInput.checkValidity();
}

/** Returns whether the user can advance from the current step (validation passed). @param {ThuysversSurveyFormContext} ctx @returns {boolean} */
function canAdvanceFromActiveStep(ctx) {
  const activeStep = getActiveStep(ctx);
  if (!activeStep) return false;

  const fieldType = activeStep.dataset?.fieldType;
  if (fieldType === 'Email') {
    const emailInput = activeStep.querySelector('input[type="email"]');
    if (!(emailInput instanceof HTMLInputElement)) return false;
    return syncEmailValidity(emailInput);
  }

    /** @type {HTMLInputElement[]} */
    const radioInputs = Array.from(activeStep.querySelectorAll('input[type="radio"]'));
    if (radioInputs.length) {
      const anyChecked = radioInputs.some((el) => el.checked);
      const firstRadio = radioInputs[0];
      if (firstRadio) firstRadio.setCustomValidity(anyChecked ? '' : 'Please select an option.');
    return anyChecked;
  }

    /** @type {HTMLInputElement[]} */
    const checkboxInputs = Array.from(activeStep.querySelectorAll('input[type="checkbox"]'));
    if (checkboxInputs.length) {
      const checkedCount = checkboxInputs.reduce((acc, el) => acc + (el.checked ? 1 : 0), 0);
      const requiredMin = 4;
      const firstCheckbox = checkboxInputs[0];
      if (firstCheckbox) firstCheckbox.setCustomValidity(checkedCount >= requiredMin ? '' : `Selecteer minimaal ${requiredMin} opties.`);
    return checkedCount >= requiredMin;
  }

  const requiredField = activeStep.querySelector('input[required], select[required], textarea[required]');
  if (requiredField instanceof HTMLInputElement || requiredField instanceof HTMLSelectElement || requiredField instanceof HTMLTextAreaElement) {
    return requiredField.checkValidity();
  }

  return true;
}

/** Returns the Gadget progress endpoint URL from form data attribute, if configured. @param {ThuysversSurveyFormContext} ctx @returns {string} */
function getGadgetProgressEndpoint(ctx) {
  const form = ctx.formEl;
  if (!form) return '';

  const explicit = String(form.getAttribute('data-gadget-progress-endpoint') || '').trim();
  if (explicit) return explicit;

  const submitEndpoint = String(form.getAttribute('data-gadget-endpoint') || '').trim();
  if (!submitEndpoint) return '';

  return '';
}

/** Returns the Gadget survey status endpoint URL from form data attribute, if configured. @param {ThuysversSurveyFormContext} ctx @returns {string} */
function getGadgetStatusEndpoint(ctx) {
  const form = ctx.formEl;
  if (!form) return '';
  return String(form.getAttribute('data-gadget-status-endpoint') || '').trim();
}

/** Returns the logged-in customer GraphQL ID from form data attribute when available. @param {ThuysversSurveyFormContext} ctx @returns {string | null} */
function getCustomerGidFromForm(ctx) {
  const form = ctx.formEl;
  if (!form) return null;
  const raw = String(form.getAttribute('data-customer-gid') || '').trim();
  return raw ? raw : null;
}

/** Extracts the submitter email from the form (FormData or email input). @param {ThuysversSurveyFormContext} ctx @returns {string} */
function getSubmitterEmailFromForm(ctx) {
  const form = ctx.formEl;
  if (!form) return '';
  try {
    const fd = new FormData(form);
    const emailInput = form.querySelector('input[type="email"]');
    return (
      String(fd.get('email') || '').trim() ||
      (emailInput instanceof HTMLInputElement ? emailInput.value.trim() : '')
    );
  } catch {
    return '';
  }
}

/** Finds the index of the SurveyStatus guard step in the survey flow. @param {ThuysversSurveyFormContext} ctx @returns {number} */
function getSurveyStatusStepIndex(ctx) {
  const steps = ctx.refs.surveyStep || [];
  return steps.findIndex((/** @type {HTMLElement} */ s) => String(s?.dataset?.fieldType || '') === 'SurveyStatus');
}

/** Enables or disables the SurveyStatus guard step in the navigation flow. @param {ThuysversSurveyFormContext} ctx @param {boolean} enabled */
function setSurveyStatusStepEnabled(ctx, enabled) {
  const idx = getSurveyStatusStepIndex(ctx);
  const step = idx >= 0 ? ctx.refs.surveyStep?.[idx] : null;
  if (!step) return;
  if (enabled) step.removeAttribute('data-skipped');
  else step.setAttribute('data-skipped', '');
}

/** Parses a quantity input value as an integer; returns 0 if invalid. @param {HTMLInputElement} input @returns {number} */
function getQtyInputNumber(input) {
  const raw = input.value;
  const num = Number.parseInt(String(raw ?? '').trim(), 10);
  return Number.isFinite(num) ? num : 0;
}

/** Shows the native validation popup when quantity is outside min/max range (does not clamp value). @param {HTMLInputElement} input */
function reportQtyRangeValidity(input) {
  if (String(input.value || '').trim() === '') return;

  if (input.validity.rangeUnderflow || input.validity.rangeOverflow) {
    input.reportValidity();
  }
}

/** Increments or decrements a quantity input by delta, respecting min/max/step. @param {ThuysversSurveyFormContext} ctx @param {HTMLInputElement} input @param {number} delta */
function bumpQtyInputValue(ctx, input, delta) {
  const min = Number.isFinite(Number(input.min)) ? Number(input.min) : 0;
  const max = input.max !== '' && Number.isFinite(Number(input.max)) ? Number(input.max) : Infinity;
  const step = Number.isFinite(Number(input.step)) && Number(input.step) > 0 ? Number(input.step) : 1;
  const current = getQtyInputNumber(input);
  const next = Math.min(max, Math.max(min, current + delta * step));

  input.value = String(next);
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

/** Returns a localized error message for submit failures based on HTTP status. @param {number | undefined} status @returns {string} */
function getSubmitErrorMessage(status) {
  if (!status) return 'Er ging iets mis bij het versturen van de enquête. Probeer het opnieuw.';
  if (status >= 500) return 'De server is tijdelijk niet beschikbaar. Probeer het later opnieuw.';
  if (status === 429) return 'Je hebt te veel verzoeken gedaan. Probeer het zo nog eens.';
  if (status >= 400) return 'Het versturen is niet gelukt. Controleer je invoer en probeer het opnieuw.';
  return 'Er ging iets mis bij het versturen van de enquête. Probeer het opnieuw.';
}

/** Persists the survey payload to sessionStorage under "recommendations" and dispatches update event. @param {unknown} payload */
function persistRecommendationsPayload(payload) {
  try {
    sessionStorage.setItem(STORAGE_KEY_RECOMMENDATIONS, JSON.stringify(payload || {}));
    window.dispatchEvent(new Event(EVENT_RECOMMENDATIONS_UPDATED));
  } catch (error) {
    console.warn('[THUYSVERS-SURVEY] Failed to persist recommendations payload to sessionStorage', error);
  }
}

/** Persists the churned recommendations payload to sessionStorage. @param {unknown} payload */
function persistChurnedRecommendationsPayload(payload) {
  try {
    sessionStorage.setItem(CHURNED_RECOMMENDATIONS, JSON.stringify(payload || {}));
  } catch (error) {
    console.warn('[THUYSVERS-SURVEY] Failed to persist churned recommendations payload to sessionStorage', error);
  }
}

/** Reads the selected profile count from the form; clamps to 1–4. @param {ThuysversSurveyFormContext} ctx @returns {number} */
function getSelectedProfileCount(ctx) {
  const form = ctx.formEl;
  if (!form) return 1;

  const directChecked = form.querySelector('input[name="profileCount"]:checked');
  const personChecked = form.querySelector('input[name^="profileCount"]:checked');
  const directField = form.querySelector('[name="profileCount"]');
  const personField = form.querySelector('[name^="profileCount"]');

  const field = (directChecked || personChecked || directField || personField);
  if (!field) return 1;

  let raw = '';
  if (field instanceof HTMLSelectElement) {
    raw = field.value;
  } else if (field instanceof HTMLInputElement) {
    raw = field.value;
  }

  const n = Number.parseInt(String(raw || '1'), 10);
  if (!Number.isFinite(n)) return 1;
  return Math.min(4, Math.max(1, n));
}

/** Marks steps with data-person > profileCount as skipped and disables their form fields. @param {ThuysversSurveyFormContext} ctx @param {number} profileCount */
function applyPersonStepSkipping(ctx, profileCount) {
  const steps = ctx.refs.surveyStep || [];
  for (const step of steps) {
    const person = Number.parseInt(String(step.dataset?.person || ''), 10);
    const isPersonStep = Number.isFinite(person) && person > 0;
    if (!isPersonStep) continue;

    const shouldSkip = person > profileCount;
    step.toggleAttribute('data-skipped', shouldSkip);

    const fields = Array.from(step.querySelectorAll('input, select, textarea')).filter(
      (el) => el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement
    );

    for (const el of fields) {
      el.disabled = shouldSkip;
    }
  }
}

/** Finds the next visible step index in the given direction (skips steps with data-skipped). @param {ThuysversSurveyFormContext} ctx @param {number} fromIndex @param {1 | -1} direction @returns {number} */
function findNextVisibleStepIndex(ctx, fromIndex, direction) {
  const steps = ctx.refs.surveyStep || [];
  let i = fromIndex + direction;
  while (i >= 0 && i < steps.length) {
    const step = steps[i];
    if (!step) {
      i += direction;
      continue;
    }
    const isSkipped = step.hasAttribute('data-skipped');
    if (!isSkipped) return i;
    i += direction;
  }
  return -1;
}

/** Finds the last step index with data-person equal to profileCount (and not skipped). @param {ThuysversSurveyFormContext} ctx @param {number} profileCount @returns {number} */
function findLastPersonStepIndex(ctx, profileCount) {
  const steps = ctx.refs.surveyStep || [];
  let last = -1;
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    if (!step) continue;
    const person = Number.parseInt(String(step.dataset?.person || ''), 10);
    if (person === profileCount && !step.hasAttribute('data-skipped')) last = i;
  }
  return last;
}

/** Collects add-ons from quantity inputs (soup, dessert, etc.) with optional soup size/unit. @param {ThuysversSurveyFormContext} ctx @param {HTMLFormElement} form @returns {unknown[]} */
function collectAddOns(ctx, form) {
  const addOns = [];
  /** @type {HTMLInputElement[]} */
  const qtyInputs = Array.from(form.querySelectorAll('input.thuysvers-survey-form-qty-input[type="number"]'));

  for (const input of qtyInputs) {
    const rawName = (input.name || '').toLowerCase().trim();
    if (!rawName) continue;

    const qty = getQtyInputNumber(input);
    if (qty <= 0) continue;

    const addonType =
      rawName.includes('soep') || rawName.includes('soup')
        ? 'soup'
        : rawName.includes('dessert')
          ? 'dessert'
          : rawName;

    let options;
    if (addonType === 'soup') {
      const extrasWrapper = input.closest('.thuysvers-survey-form-extras-wrapper');
      const checked =
        extrasWrapper?.querySelector('.thuysvers-survey-form-soup-type-wrapper input[type="radio"]:checked') ||
        form.querySelector('input[type="radio"][name*="soep-type"]:checked') ||
        form.querySelector('input[type="radio"][name*="soup-type"]:checked');

      if (checked instanceof HTMLInputElement) {
        const parsed = parseSizeUnit(checked.value);
        if (parsed.size || parsed.unit) options = parsed;
      }
    }

    const entry = { addonType, quantity: qty };
    if (options) {
      /** @type {any} */ (entry).options = options;
    }
    addOns.push(entry);
  }

  return addOns;
}

/** Parses strings like "550g", "550 gram" into { size, unit }. @param {string} raw @returns {{ size: string, unit: string }} */
function parseSizeUnit(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return { size: '', unit: '' };

  const match = s.match(/(\d+(?:[.,]\d+)?)\s*([a-zA-Z]+)/);
  if (!match) return { size: s, unit: '' };

  const size = (match[1] || '').replace(',', '.');
  let unit = (match[2] || '').toLowerCase();

  if (unit === 'g' || unit === 'gr' || unit === 'gram' || unit === 'grams') unit = 'gram';
  if (unit === 'l' || unit === 'liter' || unit === 'litre' || unit === 'liters' || unit === 'litres') unit = 'liters';

  return { size, unit };
}

/** Shows the survey form (removes hidden class, adds scroll-lock). @param {HTMLElement} ctx */
function showSurveyForm(ctx) {
  document.documentElement.setAttribute('scroll-lock', '');
  ctx.classList.remove('no-disp');
}

/** Hides the survey form and removes scroll-lock. @param {HTMLElement} ctx */
function closeSurveyForm(ctx) {
  document.documentElement.removeAttribute('scroll-lock');
  ctx.classList.add('no-disp');
}

/**
 * Checks if fieldType is a guard status (SurveyStatus or FirmhouseSubscriptionStatus).
 * @param {string | undefined} fieldType
 * @returns {boolean}
 */
function isGuardStatusFieldType(fieldType) {
  const ft = String(fieldType || '').trim();
  return ft === 'SurveyStatus' || ft === 'FirmhouseSubscriptionStatus';
}

/**
 * Updates nav/footer visibility and next button state based on active step.
 * @param {ThuysversSurveyFormContext & { refs: object }} ctx
 * @param {{ isGuardCheck?: (ft: string | undefined) => boolean }} [opts]
 */
function updateNavState(ctx, opts) {
  const activeStep = getActiveStep(ctx);
  if (!activeStep) return;

  const isGuardCheck = opts?.isGuardCheck ?? ((ft) => ft === 'SurveyStatus');
  const activeIndex = getActiveStepIndex(ctx);

  if (activeStep.dataset?.fieldType === 'Submit') {
    ctx.refs.surveyFooterContentWrapper?.classList.add('no-disp');
    ctx.refs.prevSurveyButton?.classList.add('visibility-hidden');
    ctx.refs.closeSurveyButton?.classList.add('no-disp');
  } else if (activeIndex <= 0) {
    ctx.refs.surveyFooterContentWrapper?.classList.add('no-disp');
    ctx.refs.closeSurveyButton?.classList.remove('no-disp');
  } else if (isGuardCheck(activeStep.dataset?.fieldType)) {
    // Guard steps are shown without visiting a prior step first (e.g. logged-in + active subscription).
    // Footer wrapper starts with no-disp; we must show it so the Next CTA is visible.
    ctx.refs.surveyFooterContentWrapper?.classList.remove('no-disp');
    ctx.refs.prevSurveyButton?.classList.add('visibility-hidden');
    ctx.refs.closeSurveyButton?.classList.add('no-disp');
  } else {
    ctx.refs.surveyFooterContentWrapper?.classList.remove('no-disp');
    ctx.refs.prevSurveyButton?.classList.remove('visibility-hidden');
    ctx.refs.closeSurveyButton?.classList.remove('no-disp');
  }

  const nextLabel = activeStep.dataset?.nextLabel;
  if (ctx.refs.nextSurveyButton && nextLabel) {
    ctx.refs.nextSurveyButton.textContent = nextLabel;
  }
  if (ctx.refs.nextSurveyButton) {
    ctx.refs.nextSurveyButton.disabled = !canAdvanceFromActiveStep(ctx);
  }
}

/**
 * Binds +/- buttons and quantity inputs. Optionally calls onInteraction when values change.
 * @param {ThuysversSurveyFormContext} ctx
 * @param {{ onInteraction?: () => void }} [opts]
 */
function bindQtyControlsStatic(ctx, opts) {
  const qtyBtnHandlers = ctx.qtyBtnHandlers;
  const qtyInputHandlers = ctx.qtyInputHandlers;
  if (!qtyBtnHandlers || !qtyInputHandlers) return;

  const incButtons = (Array.isArray(ctx.refs.qtyBtnInc) ? ctx.refs.qtyBtnInc : []).filter((/** @type {unknown} */ b) => b instanceof HTMLButtonElement);
  const decButtons = (Array.isArray(ctx.refs.qtyBtnDec) ? ctx.refs.qtyBtnDec : []).filter((/** @type {unknown} */ b) => b instanceof HTMLButtonElement);
  const inputs = Array.isArray(ctx.refs.qtyInput) ? ctx.refs.qtyInput : [];
  const onInteraction = opts?.onInteraction;

  /** @param {HTMLButtonElement} btn @param {number} delta */
  const bindButton = (btn, delta) => {
    if (qtyBtnHandlers.has(btn)) return;

    /** @param {Event} event */
    const handler = (event) => {
      event.preventDefault();
      const qtyWrapper = btn.closest('.thuysvers-survey-form-qty');
      if (!(qtyWrapper instanceof HTMLElement)) return;
      const inputEl = qtyWrapper.querySelector('input.thuysvers-survey-form-qty-input');
      if (!(inputEl instanceof HTMLInputElement)) return;
      bumpQtyInputValue(ctx, inputEl, delta);
      onInteraction?.();
    };

    qtyBtnHandlers.set(btn, handler);
    btn.addEventListener('click', handler);
  };

  for (const btn of incButtons) bindButton(btn, 1);
  for (const btn of decButtons) bindButton(btn, -1);

  for (const input of inputs) {
    if (!(input instanceof HTMLInputElement)) continue;
    if (qtyInputHandlers.has(input)) continue;

    /** @param {Event} _event */
    const handler = (_event) => {
      if (_event.type === 'change') reportQtyRangeValidity(input);
      onInteraction?.();
    };

    qtyInputHandlers.set(input, handler);
    input.addEventListener('input', handler);
    input.addEventListener('change', handler);
  }
}

/**
 * Builds the submission JSON from form state.
 * @param {ThuysversSurveyFormContext} ctx
 * @param {{ useExtendedEmail?: boolean; includeSubscriptionFrequency?: boolean }} [opts]
 * @returns {Record<string, unknown>}
 */
function buildSubmissionJson(ctx, opts) {
  const form = ctx.formEl;
  if (!form) return {};

  const fd = new FormData(form);
  const emailInput = form.querySelector('input[type="email"]');

  let email;
  if (opts?.useExtendedEmail) {
    const emailFromFd = String(fd.get('email') || fd.get('Email') || '').trim();
    const emailFromInput = emailInput instanceof HTMLInputElement ? emailInput.value.trim() : '';
    email = emailFromFd || emailFromInput;
  } else {
    email = String(fd.get('email') || '').trim() || (emailInput instanceof HTMLInputElement ? emailInput.value.trim() : '');
  }

  const profileCount = Number.parseInt(String(fd.get('profileCount') || '1'), 10) || getSelectedProfileCount(ctx);

  /** @param {number} p @param {string[]} candidates @returns {string} */
  const getFirstForPerson = (p, candidates) => {
    const suffix = `-p${p}`;
    for (const base of candidates) {
      const v = fd.get(`${base}${suffix}`);
      const s = String(v ?? '').trim();
      if (s !== '') return s;
    }
    return '';
  };

  /** @param {number} p @param {string[]} candidates @returns {string[]} */
  const getAllForPerson = (p, candidates) => {
    const suffix = `-p${p}`;
    for (const base of candidates) {
      const arr = fd.getAll(`${base}${suffix}`).map((x) => String(x));
      if (arr.length) return arr;
    }
    return [];
  };

  const profiles = [];
  for (let p = 1; p <= profileCount; p++) {
    const displayName = getFirstForPerson(p, ['displayName', 'name', 'fullName', 'display_name', 'full_name']);
    const mealsPerWeekRaw = getFirstForPerson(p, ['mealsPerWeek', 'meals_per_week', 'meals']);
    const mealsPerWeek = Number.parseInt(mealsPerWeekRaw || '0', 10) || 0;
    const portionSizeRaw = getFirstForPerson(p, ['portionSize', 'portion_size', 'portion', 'portie']);
    const portionSize = parseSizeUnit(portionSizeRaw);
    const diet = getFirstForPerson(p, ['dietType', 'diet_type', 'diet']);
    const dietType = Array.from({ length: Math.max(0, mealsPerWeek) }, () => ({ diet, portionSize }));
    const typeOfKitchen = getAllForPerson(p, ['typeOfKitchen', 'type_of_kitchen', 'kitchen', 'kitchenType']);
    profiles.push({ profileKey: `p${p}`, displayName, mealsPerWeek, portionSize, dietType, typeOfKitchen });
  }

  const addOns = collectAddOns(ctx, form);
  const base = { submitter: { email }, profileCount, profiles, addOns };

  if (opts?.includeSubscriptionFrequency) {
    const subscriptionFrequency = String(fd.get('subscriptionFrequency') || '').trim();
    return subscriptionFrequency ? { ...base, subscriptionFrequency } : base;
  }
  return base;
}

/**
 * Calls Gadget status endpoint. When includeSubscriptionFields is true, returns extended shape for subscription flow.
 * @param {ThuysversSurveyFormContext} ctx
 * @param {string} email
 * @param {{ includeSubscriptionFields?: boolean; emailRegex: RegExp }} [opts]
 * @returns {Promise<{
 *   hasTakenSurvey: boolean;
 *   payload: unknown;
 *   subscriptionSurveyResult?: unknown;
 *   hasActivatedFirmhouseSubscription?: boolean;
 *   hasOngoingFirmhouseSubscription?: boolean;
 * }>}
 */
async function fetchSurveyStatus(ctx, email, opts) {
  const endpoint = getGadgetStatusEndpoint(ctx);
  const includeSubscriptionFields = opts?.includeSubscriptionFields ?? false;
  const emailRegex = opts?.emailRegex ?? EMAIL_REGEX;

  const emptyResult = includeSubscriptionFields
    ? {
        hasTakenSurvey: false,
        payload: null,
        subscriptionSurveyResult: null,
        hasActivatedFirmhouseSubscription: false,
        hasOngoingFirmhouseSubscription: false,
      }
    : { hasTakenSurvey: false, payload: null };

  if (!endpoint) return /** @type {typeof emptyResult} */ (emptyResult);

  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail || !emailRegex.test(normalizedEmail)) return /** @type {typeof emptyResult} */ (emptyResult);

  const customerGid = getCustomerGidFromForm(ctx);
  const form = ctx.formEl;
  const shopId = form ? (String(form.getAttribute('data-shop-id') || '').trim() || undefined) : undefined;

  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        customerGid,
        email: normalizedEmail,
        includeData: true,
        ...(shopId ? { shopId } : {}),
      }),
    });

    if (!res.ok) {
      console.warn('[THUYSVERS-SURVEY] Survey status request failed', { status: res.status });
      return /** @type {typeof emptyResult} */ (emptyResult);
    }

    const data = await res.json().catch(() => null);
    const hasTakenSurvey = Boolean(data && typeof data === 'object' && /** @type {any} */ (data).hasTakenSurvey);
    const rawPayload =
      data && typeof data === 'object'
        ? /** @type {any} */ (data).mijn_gegevens ?? /** @type {any} */ (data).payload ?? null
        : null;
    const churnedRecommendationsRawPayload = data && typeof data === 'object' ? /** @type {any} */ (data).churnedRecommendations ?? null : null;
    const payloadLooksValid =
      rawPayload &&
      typeof rawPayload === 'object' &&
      Array.isArray(/** @type {any} */ (rawPayload).profiles);

    if (churnedRecommendationsRawPayload) persistChurnedRecommendationsPayload(churnedRecommendationsRawPayload);

    if (!includeSubscriptionFields) {
      return { hasTakenSurvey, payload: payloadLooksValid ? rawPayload : null };
    }

    const subscriptionSurveyResult =
      data && typeof data === 'object'
        ? /** @type {any} */ (data).subscription_survey_result ?? /** @type {any} */ (data).subscriptionSurveyResult ?? null
        : null;

    const hasOngoingFirmhouseSubscription = Boolean(
      data &&
        typeof data === 'object' &&
        (/** @type {any} */ (data).hasOngoingFirmhouseSubscription ??
          /** @type {any} */ (data).has_ongoing_firmhouse_subscription ??
          false)
    );

    const firmhouseSubscription =
      data && typeof data === 'object'
        ? /** @type {any} */ (data).firmhouseSubscription ?? /** @type {any} */ (data).firmhouse_subscription ?? null
        : null;

    const explicitActivated = Boolean(
      data &&
        typeof data === 'object' &&
        (/** @type {any} */ (data).hasActivatedFirmhouseSubscription ??
          /** @type {any} */ (data).has_activated_firmhouse_subscription ??
          false)
    );

    const inferredActivated = (() => {
      if (!firmhouseSubscription || typeof firmhouseSubscription !== 'object') return false;
      const statusRaw = String(
        /** @type {any} */ (firmhouseSubscription).status ??
          /** @type {any} */ (firmhouseSubscription).state ??
          /** @type {any} */ (firmhouseSubscription).subscriptionStatus ??
          ''
      )
        .trim()
        .toLowerCase();
      return statusRaw === 'activated' || statusRaw === 'active';
    })();

    const hasActivatedFirmhouseSubscription = explicitActivated || inferredActivated;

    return {
      hasTakenSurvey,
      payload: payloadLooksValid ? rawPayload : null,
      subscriptionSurveyResult: subscriptionSurveyResult && typeof subscriptionSurveyResult === 'object' ? subscriptionSurveyResult : null,
      hasActivatedFirmhouseSubscription,
      hasOngoingFirmhouseSubscription,
    };
  } catch (error) {
    console.warn('[THUYSVERS-SURVEY] Survey status request errored', error);
    return /** @type {typeof emptyResult} */ (emptyResult);
  }
}

/**
 * Submits payload to Gadget endpoint. Optionally calls onSuccessPersist after persisting recommendations.
 * @param {ThuysversSurveyFormContext & { formEl: HTMLFormElement | null; isSubmitting?: boolean }} ctx
 * @param {unknown} payload
 * @param {{ onSuccessPersist?: (p: unknown) => void }} [opts]
 * @returns {Promise<{ ok: boolean; status?: number }>}
 */
async function submitToGadgetEndpoint(ctx, payload, opts) {
  const form = ctx.formEl;
  if (!form) return { ok: false };
  if (ctx.isSubmitting) return { ok: false };

  ctx.isSubmitting = true;
  try {
    const endpoint = String(form.getAttribute('data-gadget-endpoint') || '').trim();
    if (!endpoint) {
      console.warn('[THUYSVERS-SURVEY] No gadget endpoint configured; skipping submission');
      return { ok: true };
    }

    const shopId = String(form.getAttribute('data-shop-id') || '').trim() || undefined;
    const finalPayload = {
      submissionId: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(shopId ? { shopId } : {}),
      ...(typeof payload === 'object' && payload !== null ? payload : {}),
    };

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(finalPayload),
    });

    if (!res.ok) {
      console.error('[THUYSVERS-SURVEY] Submission request failed', { status: res.status });
      return { ok: false, status: res.status };
    }

    const contentType = res.headers.get('content-type') || '';
    const body = contentType.includes('application/json') ? await res.json().catch(() => null) : await res.text().catch(() => '');
    void body;

    persistRecommendationsPayload(payload);
    opts?.onSuccessPersist?.(payload);
    return { ok: true };
  } catch (error) {
    console.error('[THUYSVERS-SURVEY] Failed to submit to gadget endpoint', error);
    return { ok: false };
  } finally {
    ctx.isSubmitting = false;
  }
}

/**
 * Runs the async submit-step flow: submit, then update UI (loader, heading).
 * @param {{
 *   refs: { surveySubmitWrapper?: HTMLElement; surveyFooterContentWrapper?: HTMLElement };
 *   getActiveStep?: () => HTMLElement | undefined;
 *   getSubmitterEmailFromForm?: () => string;
 *   getSubmitErrorMessage?: (status?: number) => string;
 *   submitToGadgetEndpoint: (p: unknown) => Promise<{ ok: boolean; status?: number }>;
 * }} ctx
 * @param {Record<string, unknown>} payload
 * @param {{
 *   getCompletedEmail: (p: Record<string, unknown>, getFormEmail: () => string) => string;
 *   storageKeyCompleted: string;
 *   emailRegex: RegExp;
 *   sendProgress?: (email: string, extra?: Record<string, unknown>) => void | Promise<void>;
 * }} opts
 */
function runSubmitStepAsync(ctx, payload, opts) {
  const mainSubmitHeading = ctx.getActiveStep?.()?.querySelector('.thuysvers-survey-form-heading:not(.thuysvers-survey-form-heading-submit)');

  if (mainSubmitHeading instanceof HTMLElement && !mainSubmitHeading.dataset.originalText) {
    mainSubmitHeading.dataset.originalText = mainSubmitHeading.textContent || '';
  }

  ctx.refs.surveySubmitWrapper?.classList.remove('no-disp');
  ctx.refs.surveyFooterContentWrapper?.classList.add('no-disp');
  mainSubmitHeading?.classList.add('no-disp');

  void (async () => {
    const result = await ctx.submitToGadgetEndpoint(payload);

    if (result.ok) {
      const email = opts.getCompletedEmail(
        /** @type {Record<string, unknown>} */ (payload),
        () => (ctx.getSubmitterEmailFromForm ? ctx.getSubmitterEmailFromForm() : '')
      );
      if (email && opts.emailRegex.test(String(email))) {
        try {
          sessionStorage.setItem(opts.storageKeyCompleted, String(email));
        } catch {
          // ignore
        }
        const subId = /** @type {{ submissionId?: string }} */ (payload)?.submissionId;
        if (opts.sendProgress) {
          void opts.sendProgress(String(email), subId != null ? { submissionId: String(subId) } : undefined);
        }
      }
    }

    const currentActive = ctx.getActiveStep?.();
    if (currentActive?.dataset?.fieldType !== 'Submit') return;

    ctx.refs.surveySubmitWrapper?.classList.add('no-disp');
    ctx.refs.surveyFooterContentWrapper?.classList.remove('no-disp');

    if (mainSubmitHeading instanceof HTMLElement) {
      mainSubmitHeading.textContent = result.ok
        ? (mainSubmitHeading.dataset.originalText || mainSubmitHeading.textContent || '')
        : (ctx.getSubmitErrorMessage ? ctx.getSubmitErrorMessage(result.status) : getSubmitErrorMessage(result.status));
      mainSubmitHeading.classList.remove('no-disp');
    }
  })();
}

/** @type {{
 *   EMAIL_REGEX: RegExp;
 *   getActiveStep: (ctx: object) => HTMLElement | undefined;
 *   getActiveStepIndex: (ctx: object) => number;
 *   setActiveStepIndex: (ctx: object, index: number) => void;
 *   syncEmailValidity: (emailInput: HTMLInputElement) => boolean;
 *   canAdvanceFromActiveStep: (ctx: object) => boolean;
 *   getGadgetProgressEndpoint: (ctx: object) => string;
 *   getGadgetStatusEndpoint: (ctx: object) => string;
 *   getCustomerGidFromForm: (ctx: object) => string | null;
 *   getSubmitterEmailFromForm: (ctx: object) => string;
 *   getSurveyStatusStepIndex: (ctx: object) => number;
 *   setSurveyStatusStepEnabled: (ctx: object, enabled: boolean) => void;
 *   getQtyInputNumber: (input: HTMLInputElement) => number;
 *   reportQtyRangeValidity: (input: HTMLInputElement) => void;
 *   bumpQtyInputValue: (ctx: object, input: HTMLInputElement, delta: number) => void;
 *   getSubmitErrorMessage: (status?: number) => string;
 *   persistRecommendationsPayload: (payload: unknown) => void;
 *   persistChurnedRecommendationsPayload: (payload: unknown) => void;
 *   getSelectedProfileCount: (ctx: object) => number;
 *   applyPersonStepSkipping: (ctx: object, profileCount: number) => void;
 *   findNextVisibleStepIndex: (ctx: object, fromIndex: number, direction: 1 | -1) => number;
 *   findLastPersonStepIndex: (ctx: object, profileCount: number) => number;
 *   collectAddOns: (ctx: object, form: HTMLFormElement) => unknown[];
 *   parseSizeUnit: (raw: string) => { size: string; unit: string };
 *   showSurveyForm: (ctx: HTMLElement) => void;
 *   closeSurveyForm: (ctx: HTMLElement) => void;
 *   isGuardStatusFieldType: (fieldType: string | undefined) => boolean;
 *   updateNavState: (ctx: object, opts?: { isGuardCheck?: (ft: string | undefined) => boolean }) => void;
 *   bindQtyControlsStatic: (ctx: object, opts?: { onInteraction?: () => void }) => void;
 *   buildSubmissionJson: (ctx: object, opts?: object) => Record<string, unknown>;
 *   submitToGadgetEndpoint: (ctx: object, payload: unknown, opts?: object) => Promise<{ ok: boolean; status?: number }>;
 *   runSubmitStepAsync: (ctx: object, payload: Record<string, unknown>, opts: object) => void;
 *   fetchSurveyStatus: (ctx: object, email: string, opts?: object) => Promise<{
 *     hasTakenSurvey: boolean;
 *     payload: unknown;
 *     subscriptionSurveyResult?: unknown;
 *     hasActivatedFirmhouseSubscription?: boolean;
 *     hasOngoingFirmhouseSubscription?: boolean;
 *   }>;
 * }} */
const utilsExport = {
  EMAIL_REGEX,
  getActiveStep,
  getActiveStepIndex,
  setActiveStepIndex,
  syncEmailValidity,
  canAdvanceFromActiveStep,
  getGadgetProgressEndpoint,
  getGadgetStatusEndpoint,
  getCustomerGidFromForm,
  getSubmitterEmailFromForm,
  getSurveyStatusStepIndex,
  setSurveyStatusStepEnabled,
  getQtyInputNumber,
  reportQtyRangeValidity,
  bumpQtyInputValue,
  getSubmitErrorMessage,
  persistRecommendationsPayload,
  persistChurnedRecommendationsPayload,
  getSelectedProfileCount,
  applyPersonStepSkipping,
  findNextVisibleStepIndex,
  findLastPersonStepIndex,
  collectAddOns,
  parseSizeUnit,
  showSurveyForm,
  closeSurveyForm,
  isGuardStatusFieldType,
  updateNavState,
  bindQtyControlsStatic,
  buildSubmissionJson,
  submitToGadgetEndpoint,
  runSubmitStepAsync,
  fetchSurveyStatus,
};

window.ThuysversSurveyFormUtils = utilsExport;
})();
